/**
 * 売上連動 累進ティアバック計算ユーティリティ v3.0.0
 *
 * Shop.incentiveSettings の階段定義に従い、当期累計売上から rate を判定。
 *
 * 【正社員ロジック】 4段:
 *   累計 < monthlySalary           → belowSalaryRate
 *   monthlySalary 〜 tier1Threshold → aboveSalaryRate
 *   tier1Threshold 〜 tier2Threshold → tier1Rate
 *   tier2Threshold 〜              → tier2Rate
 *
 * 【アルバイトロジック】 3段:
 *   日平均累計 < hourlyWage相当    → belowWageRate
 *   日平均累計 < dailyAvgThreshold → lowRate
 *   日平均累計 >= dailyAvgThreshold → highRate
 */

export interface IncentiveSettings {
  enabled?: boolean;
  /**
   * 累進の当て方。
   * 'retroactive'(既定) … 当期累計で決めたレートを累計全体に当て、既払いとの差額を支払う。
   *                        「月商100万超なら全体が25%」という運用をそのまま保ちつつ、
   *                        伝票の切り方で総額が変わらない。
   * 'marginal'          … 各帯に入った額だけへその帯のレートを当てる（帯ごとの累進）。
   * 'wholeAtTier'       … 旧挙動。会計ごとにその時点のレートを当てる。
   *                        同じ月商でも分割するほど減るため、互換目的以外では使わない。
   */
  mode?: 'wholeAtTier' | 'marginal' | 'retroactive';
  partTimeRules?: {
    dailyAvgThreshold?: number;
    lowRate?: number;
    highRate?: number;
    belowWageRate?: number;
    /**
     * 「時給未満」を判定するときの1日の想定勤務時間。
     * 既定 6.0（夜職の実働に合わせる）。旧実装は 8 固定で、
     * 実働6時間の店では床が実際の日給より高くなり本来15%のキャストが10%に落ちていた。
     */
    standardDailyHours?: number;
  };
  regularRules?: {
    belowSalaryRate?: number;
    aboveSalaryRate?: number;
    tier1Threshold?: number;
    tier1Rate?: number;
    tier2Threshold?: number;
    tier2Rate?: number;
  };
}

export interface StaffSnapshot {
  id: string;
  name?: string;
  role?: 'owner' | 'manager' | 'staff' | 'parttime';
  monthlySalary?: number;  // 正社員の月給（基準ライン）
  hourlyWage?: number;     // アルバイトの時給
}

export interface ProgressiveTierResult {
  rate: number;            // 採用された rate (0-1)
  tier: 'belowSalary' | 'aboveSalary' | 'tier1' | 'tier2'
       | 'belowWage' | 'low' | 'high'
       | 'default';
  label: string;
  nextThreshold?: number;  // 次ティアまでの差分参考用
  nextRate?: number;
}

/**
 * 正社員の累進ティアバック判定
 * @param cumulativeSales 当期累計売上（インセンティブベース、円）
 * @param staff スタッフ（monthlySalary 推奨）
 * @param settings shop.incentiveSettings.regularRules
 */
export function calculateRegularRate(
  cumulativeSales: number,
  staff: StaffSnapshot,
  settings: IncentiveSettings['regularRules'] = {}
): ProgressiveTierResult {
  const monthlySalary = staff.monthlySalary ?? 0;
  const tier1 = settings.tier1Threshold ?? 600000;
  const tier2 = settings.tier2Threshold ?? 1000000;
  const belowSalaryRate = settings.belowSalaryRate ?? 0.10;
  const aboveSalaryRate = settings.aboveSalaryRate ?? 0.15;
  const tier1Rate = settings.tier1Rate ?? 0.20;
  const tier2Rate = settings.tier2Rate ?? 0.25;

  if (cumulativeSales >= tier2) {
    return { rate: tier2Rate, tier: 'tier2', label: `Tier2 (${pct(tier2Rate)})` };
  }
  if (cumulativeSales >= tier1) {
    return {
      rate: tier1Rate,
      tier: 'tier1',
      label: `Tier1 (${pct(tier1Rate)})`,
      nextThreshold: tier2,
      nextRate: tier2Rate,
    };
  }
  if (cumulativeSales >= monthlySalary && monthlySalary > 0) {
    return {
      rate: aboveSalaryRate,
      tier: 'aboveSalary',
      label: `月給超 (${pct(aboveSalaryRate)})`,
      nextThreshold: tier1,
      nextRate: tier1Rate,
    };
  }
  return {
    rate: belowSalaryRate,
    tier: 'belowSalary',
    label: `月給以下 (${pct(belowSalaryRate)})`,
    nextThreshold: monthlySalary > 0 ? monthlySalary : tier1,
    nextRate: monthlySalary > 0 ? aboveSalaryRate : tier1Rate,
  };
}

/**
 * アルバイトの累進ティアバック判定
 * @param cumulativeSales 当期累計売上
 * @param workedDays 当期出勤日数（日平均算出用、0除算回避は呼出側で）
 * @param staff スタッフ（hourlyWage 推奨）
 * @param settings shop.incentiveSettings.partTimeRules
 */
export function calculatePartTimeRate(
  cumulativeSales: number,
  workedDays: number,
  staff: StaffSnapshot,
  settings: IncentiveSettings['partTimeRules'] = {}
): ProgressiveTierResult {
  const dailyAvg = workedDays > 0 ? cumulativeSales / workedDays : 0;
  const threshold = settings.dailyAvgThreshold ?? 30000;
  const low = settings.lowRate ?? 0.15;
  const high = settings.highRate ?? 0.20;
  const belowWage = settings.belowWageRate ?? 0.10;

  // 時給未満の判定: 日平均が hourlyWage × 標準勤務時間 未満なら時給未満扱い
  const standardHours = settings.standardDailyHours ?? DEFAULT_STANDARD_DAILY_HOURS;
  const dailyEarnFloor = (staff.hourlyWage ?? 0) * standardHours;
  if (dailyEarnFloor > 0 && dailyAvg < dailyEarnFloor) {
    return {
      rate: belowWage,
      tier: 'belowWage',
      label: `時給未満 (${pct(belowWage)})`,
      nextThreshold: dailyEarnFloor,
      nextRate: low,
    };
  }
  if (dailyAvg >= threshold) {
    return { rate: high, tier: 'high', label: `日平均閾値超 (${pct(high)})` };
  }
  return {
    rate: low,
    tier: 'low',
    label: `日平均閾値以下 (${pct(low)})`,
    nextThreshold: threshold,
    nextRate: high,
  };
}

/**
 * ロールから自動判定して適切な rate を返す
 */
export function calculateProgressiveRate(
  cumulativeSales: number,
  workedDays: number,
  staff: StaffSnapshot,
  settings: IncentiveSettings = {}
): ProgressiveTierResult {
  if (!settings.enabled) {
    return { rate: 0, tier: 'default', label: '累進無効（既定）' };
  }
  if (staff.role === 'parttime') {
    return calculatePartTimeRate(cumulativeSales, workedDays, staff, settings.partTimeRules);
  }
  // owner/manager/staff/その他 → 正社員ロジック
  return calculateRegularRate(cumulativeSales, staff, settings.regularRules);
}

function pct(r: number): string {
  return `${Math.round(r * 100)}%`;
}

/** 夜職の1シフトは5〜6時間が普通。8時間は日勤の値。 */
export const DEFAULT_STANDARD_DAILY_HOURS = 6;

/**
 * 累進の帯（正社員ロジック）。低い順に並ぶ。
 * upTo は「この額まで」、rate はその帯に入った金額へ当てるレート。
 */
export function buildRegularBands(
  staff: StaffSnapshot,
  settings: IncentiveSettings['regularRules'] = {}
): { upTo: number; rate: number; label: string }[] {
  const monthlySalary = staff.monthlySalary ?? 0;
  const tier1 = settings.tier1Threshold ?? 600000;
  const tier2 = settings.tier2Threshold ?? 1000000;
  const belowSalaryRate = settings.belowSalaryRate ?? 0.10;
  const aboveSalaryRate = settings.aboveSalaryRate ?? 0.15;
  const tier1Rate = settings.tier1Rate ?? 0.20;
  const tier2Rate = settings.tier2Rate ?? 0.25;

  const bands: { upTo: number; rate: number; label: string }[] = [];
  if (monthlySalary > 0 && monthlySalary < tier1) {
    bands.push({ upTo: monthlySalary, rate: belowSalaryRate, label: `月給以下 (${pct(belowSalaryRate)})` });
    bands.push({ upTo: tier1, rate: aboveSalaryRate, label: `月給超 (${pct(aboveSalaryRate)})` });
  } else {
    bands.push({ upTo: tier1, rate: belowSalaryRate, label: `Tier1未満 (${pct(belowSalaryRate)})` });
  }
  bands.push({ upTo: tier2, rate: tier1Rate, label: `Tier1 (${pct(tier1Rate)})` });
  bands.push({ upTo: Number.POSITIVE_INFINITY, rate: tier2Rate, label: `Tier2 (${pct(tier2Rate)})` });
  return bands;
}

/**
 * 会計1件ぶんのバック額を、帯ごとに切り分けて計算する。
 *
 * これにより「同じ月商でも伝票の切り方でバックが変わる」問題が消える。
 * 累計 cumulativeBefore の続きから amount ぶんを積み上げる。
 *
 * @returns バック額と、内訳（どの帯にいくら入ったか）
 */
export function calculateMarginalBack(
  cumulativeBefore: number,
  amount: number,
  bands: { upTo: number; rate: number; label: string }[]
): { back: number; segments: { label: string; rate: number; amount: number; back: number }[] } {
  if (amount <= 0) return { back: 0, segments: [] };
  let cursor = Math.max(0, cumulativeBefore);
  let remaining = amount;
  let back = 0;
  const segments: { label: string; rate: number; amount: number; back: number }[] = [];

  for (const band of bands) {
    if (remaining <= 0) break;
    if (cursor >= band.upTo) continue;
    const room = band.upTo - cursor;
    const take = Math.min(room, remaining);
    if (take > 0) {
      const seg = take * band.rate;
      back += seg;
      segments.push({ label: band.label, rate: band.rate, amount: take, back: seg });
      cursor += take;
      remaining -= take;
    }
  }
  return { back: Math.round(back), segments };
}

/**
 * アルバイトの帯。日平均で判定するため、当期の出勤日数が要る。
 */
export function buildPartTimeBands(
  workedDays: number,
  staff: StaffSnapshot,
  settings: IncentiveSettings['partTimeRules'] = {}
): { upTo: number; rate: number; label: string }[] {
  const days = Math.max(1, workedDays);
  const threshold = settings.dailyAvgThreshold ?? 30000;
  const low = settings.lowRate ?? 0.15;
  const high = settings.highRate ?? 0.20;
  const belowWage = settings.belowWageRate ?? 0.10;
  const standardHours = settings.standardDailyHours ?? DEFAULT_STANDARD_DAILY_HOURS;
  const wageFloorTotal = (staff.hourlyWage ?? 0) * standardHours * days;
  const thresholdTotal = threshold * days;

  const bands: { upTo: number; rate: number; label: string }[] = [];
  if (wageFloorTotal > 0 && wageFloorTotal < thresholdTotal) {
    bands.push({ upTo: wageFloorTotal, rate: belowWage, label: `時給未満 (${pct(belowWage)})` });
  }
  bands.push({ upTo: thresholdTotal, rate: low, label: `日平均閾値以下 (${pct(low)})` });
  bands.push({ upTo: Number.POSITIVE_INFINITY, rate: high, label: `日平均閾値超 (${pct(high)})` });
  return bands;
}

/**
 * ロールから帯を組み立てる。calculateProgressiveRate と同じ判定基準を使う。
 */
export function buildBands(
  workedDays: number,
  staff: StaffSnapshot,
  settings: IncentiveSettings = {}
): { upTo: number; rate: number; label: string }[] {
  if (!settings.enabled) return [{ upTo: Number.POSITIVE_INFINITY, rate: 0, label: '累進無効（既定）' }];
  if (staff.role === 'parttime') return buildPartTimeBands(workedDays, staff, settings.partTimeRules);
  return buildRegularBands(staff, settings.regularRules);
}

/**
 * 当期累計 x に対する「本来支払うべきバック総額」。
 * retroactive モードの土台。累計で決まる関数なので、
 * 会計をどう分割しても f(最終累計) は同じ = 分割不変になる。
 */
export function cumulativeBackAt(
  cumulative: number,
  workedDays: number,
  staff: StaffSnapshot,
  settings: IncentiveSettings = {}
): number {
  if (!settings.enabled || cumulative <= 0) return 0;
  const t = calculateProgressiveRate(cumulative, workedDays, staff, settings);
  return cumulative * t.rate;
}

/**
 * 会計1件ぶんのバックを計算する統一入口。
 * 既定は retroactive（分割不変・旧来の支給水準を維持）。
 */
export function calculateIncrementalBack(
  cumulativeBefore: number,
  amount: number,
  workedDays: number,
  staff: StaffSnapshot,
  settings: IncentiveSettings = {}
): {
  back: number;
  rate: number;
  tier: string;
  label: string;
  segments: { label: string; rate: number; amount: number; back: number }[];
} {
  if (!settings.enabled || amount <= 0) {
    return { back: 0, rate: 0, tier: 'default', label: '累進無効（既定）', segments: [] };
  }
  const mode = settings.mode ?? 'retroactive';

  if (mode === 'retroactive') {
    // 累計に対する総額の差分を今回ぶんとする。
    // 帯をまたいだ会計では、過去ぶんの引き上げも含めてここで支払われる。
    const before = cumulativeBackAt(cumulativeBefore, workedDays, staff, settings);
    const after = cumulativeBackAt(cumulativeBefore + amount, workedDays, staff, settings);
    const reached = calculateProgressiveRate(cumulativeBefore + amount, workedDays, staff, settings);
    const back = Math.round(after - before);
    return {
      back,
      rate: amount > 0 ? back / amount : 0,
      tier: reached.tier,
      label: reached.label,
      segments: [{ label: reached.label, rate: reached.rate, amount, back }],
    };
  }

  if (mode === 'wholeAtTier') {
    const t = calculateProgressiveRate(cumulativeBefore + amount, workedDays, staff, settings);
    return {
      back: Math.round(amount * t.rate),
      rate: t.rate,
      tier: t.tier,
      label: t.label,
      segments: [{ label: t.label, rate: t.rate, amount, back: Math.round(amount * t.rate) }],
    };
  }
  const bands = buildBands(workedDays, staff, settings);
  const { back, segments } = calculateMarginalBack(cumulativeBefore, amount, bands);
  const top = segments.length > 0 ? segments[segments.length - 1] : null;
  const effRate = amount > 0 ? back / amount : 0;
  const reached = calculateProgressiveRate(cumulativeBefore + amount, workedDays, staff, settings);
  return {
    back,
    rate: effRate,
    tier: reached.tier,
    label: top ? top.label : reached.label,
    segments,
  };
}
